import { useState, useRef, useCallback } from "react";
import Axios from "axios";
import { toast } from "react-toastify";
import {
    CONTENT_PROCESS_UPLOAD_URL,
    CONTENT_PROCESS_RENAME_URL,
    PDF_MSG_UPLOADING,
    PDF_MSG_RENAMING,
    PDF_MSG_SUCCESS,
    PDF_MSG_FAILED,
    PDF_MSG_REQUIRED,
} from "../constants";

export const PDF_STATUS = {
    Idle: "idle",
    Uploading: "uploading",
    Success: "success",
    Error: "error",
};

// PDF lecture-note state machine. Replaces the legacy sessionStorage("pdfUrl") +
// messageRef.textContent mechanics with explicit state, preserving semantics:
//  - add-flow submit gate: pdfUrl set AND status === Success
//  - submit button locked while Uploading and kept locked on Error
//  - invalidate() = the "metadata changed after upload" rule: unchecks the
//    control checkbox, clears the file input, and (in add flows) demands a
//    re-upload with the red "Lütfen Pdf Yükleyiniz!" message
export const usePdfUpload = () => {
    const adminToken = sessionStorage.getItem("jwtP");
    const [pdfUrl, setPdfUrl] = useState(null);
    const [status, setStatus] = useState(PDF_STATUS.Idle);
    const [message, setMessage] = useState("");
    const [notesChecked, setNotesChecked] = useState(false);
    const fileInputRef = useRef(null);
    // pdfUrl mirror for callbacks that outlive a render (rename is fire-and-forget)
    const pdfUrlRef = useRef(null);

    const setUrl = (url) => {
        pdfUrlRef.current = url;
        setPdfUrl(url);
    };

    // Seed with an existing LectureNoteUrl when the update modal opens
    const seed = useCallback((url) => {
        pdfUrlRef.current = url || null;
        setPdfUrl(url || null);
        setStatus(PDF_STATUS.Idle);
        setMessage("");
        setNotesChecked(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    }, []);

    const reset = useCallback(() => {
        seed(null);
    }, [seed]);

    // Duplicate flow: adopt an existing, already-live LectureNoteUrl as if it
    // had just been uploaded — the add-submit gate passes without a re-upload
    // (identical classification ⇒ the filename contract holds), while any
    // metadata change still invalidates it and forces a real upload. The
    // attestation checkbox stays unchecked.
    const seedAsUploaded = useCallback((url) => {
        if (!url) return;
        pdfUrlRef.current = url;
        setPdfUrl(url);
        setStatus(PDF_STATUS.Success);
        setMessage(PDF_MSG_SUCCESS);
        setNotesChecked(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    }, []);

    const headers = (contentType) => ({
        "Access-Control-Allow-Origin": "*",
        Authorization: "Bearer " + adminToken,
        "Content-Type": contentType,
    });

    const upload = async (file, fileName) => {
        if (!file) return;
        setStatus(PDF_STATUS.Uploading);
        setMessage(PDF_MSG_UPLOADING);

        const formDataPdf = new FormData();
        formDataPdf.append("Folder", "onlinelectures");
        formDataPdf.append("FormFile", file);
        formDataPdf.append("FileName", fileName);

        try {
            const response = await Axios.post(CONTENT_PROCESS_UPLOAD_URL, formDataPdf, {
                headers: headers("multipart/form-data"),
            });
            if (response.data.responseStatus === 0) {
                toast.error(response.data.responseMessage);
                setStatus(PDF_STATUS.Error);
                setMessage(response.data.responseMessage);
            } else {
                toast.success(PDF_MSG_SUCCESS);
                setStatus(PDF_STATUS.Success);
                setMessage(PDF_MSG_SUCCESS);
                setUrl(response.data.responseData[0].imageUrl);
            }
        } catch (error) {
            toast.error(PDF_MSG_FAILED + " ");
            setStatus(PDF_STATUS.Error);
            setMessage(PDF_MSG_FAILED);
        }
    };

    // Update-flow rename. Deliberately NOT awaited by the caller — the lecture
    // update request runs in parallel, exactly like the legacy page.
    const rename = (newFileName, onlineLectureId, onSuccess) => {
        if (!pdfUrlRef.current) return;
        setStatus(PDF_STATUS.Uploading);
        setMessage(PDF_MSG_RENAMING);

        const requestData = {
            oldFile: pdfUrlRef.current,
            newFile: newFileName,
            ...(onlineLectureId ? { OnlineLectureId: onlineLectureId } : {}),
        };

        Axios.post(CONTENT_PROCESS_RENAME_URL, requestData, {
            headers: headers("application/json"),
        })
            .then((response) => {
                if (response.data.responseStatus === 0) {
                    toast.error(response.data.responseMessage);
                    setStatus(PDF_STATUS.Error);
                    setMessage(PDF_MSG_FAILED);
                } else {
                    toast.success(PDF_MSG_SUCCESS);
                    setStatus(PDF_STATUS.Success);
                    setMessage("PDF Güncelleme başarılı");
                    if (onSuccess) onSuccess();
                }
            })
            .catch(() => {
                toast.error(PDF_MSG_FAILED + " ");
                setStatus(PDF_STATUS.Error);
                setMessage(PDF_MSG_FAILED);
            });
    };

    // "Metadata changed after a PDF exists" rule. showMessage=true in add flows
    // (red "Lütfen Pdf Yükleyiniz!"); the update flow unchecks/clears silently.
    // A Success status drops to Idle (breaks the submit gate, forces re-upload)
    // but an Error status is kept — legacy keeps the button locked after a
    // failed upload until a re-upload succeeds.
    const invalidate = useCallback((showMessage) => {
        if (!pdfUrlRef.current) return;
        setNotesChecked(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
        setStatus((current) => (current === PDF_STATUS.Error ? current : PDF_STATUS.Idle));
        if (showMessage) {
            setMessage(PDF_MSG_REQUIRED);
        }
    }, []);

    return {
        pdfUrl,
        status,
        message,
        notesChecked,
        setNotesChecked,
        fileInputRef,
        upload,
        rename,
        seed,
        seedAsUploaded,
        reset,
        invalidate,
        isUploadedOk: Boolean(pdfUrl) && status === PDF_STATUS.Success,
        submitLocked: status === PDF_STATUS.Uploading || status === PDF_STATUS.Error,
    };
};
